/**
 * In-App Notification Service
 *
 * Stores notifications in localStorage for now. When Gobodo notification
 * support is ready, swap to Gobodo CRUD calls.
 */

const STORAGE_KEY = 'crm_notifications';
const MAX_NOTIFICATIONS = 200;

export interface AppNotification {
  id: string;
  message: string;
  priority: 'Low' | 'Normal' | 'High';
  recipientType: 'recordOwner' | 'user' | 'role' | 'all';
  recipientId?: string;
  actionUrl?: string;
  workflowRuleId?: string;
  workflowRuleName?: string;
  objectApiName?: string;
  recordId?: string;
  createdAt: string;
  read: boolean;
}

function loadAll(): AppNotification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveAll(notifications: AppNotification[]): void {
  // Keep most recent
  const trimmed = notifications.slice(-MAX_NOTIFICATIONS);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
}

export const notificationService = {
  add(notification: Omit<AppNotification, 'id' | 'createdAt' | 'read'>): AppNotification {
    const all = loadAll();
    const entry: AppNotification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      createdAt: new Date().toISOString(),
      read: false,
    };
    all.push(entry);
    saveAll(all);
    return entry;
  },

  getAll(): AppNotification[] {
    return loadAll().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  /** UC12/13: Filter notifications by recipient targeting. */
  getForUser(userId: string, userRoles: string[] = [], ownedRecordIds: string[] = []): AppNotification[] {
    const ownedSet = new Set(ownedRecordIds);
    const roleSet = new Set(userRoles.map(r => r.toLowerCase()));
    return this.getAll().filter(n => {
      if (n.recipientType === 'all') return true;
      if (n.recipientType === 'user' && n.recipientId === userId) return true;
      if (n.recipientType === 'role' && n.recipientId && roleSet.has(n.recipientId.toLowerCase())) return true;
      if (n.recipientType === 'recordOwner') {
        if (n.recordId && ownedSet.has(n.recordId)) return true;
        if (!n.recipientId) return true;
        if (n.recipientId === userId) return true;
      }
      return false;
    });
  },

  getUnread(): AppNotification[] {
    return this.getAll().filter(n => !n.read);
  },

  getUnreadForUser(userId: string, userRoles: string[] = [], ownedRecordIds: string[] = []): AppNotification[] {
    return this.getForUser(userId, userRoles, ownedRecordIds).filter(n => !n.read);
  },

  getUnreadCount(): number {
    return loadAll().filter(n => !n.read).length;
  },

  getUnreadCountForUser(userId: string, userRoles: string[] = [], ownedRecordIds: string[] = []): number {
    return this.getUnreadForUser(userId, userRoles, ownedRecordIds).length;
  },

  markAsRead(id: string): void {
    const all = loadAll();
    const idx = all.findIndex(n => n.id === id);
    if (idx >= 0) {
      all[idx].read = true;
      saveAll(all);
    }
  },

  markAllAsRead(): void {
    const all = loadAll();
    all.forEach(n => { n.read = true; });
    saveAll(all);
  },

  delete(id: string): void {
    const all = loadAll().filter(n => n.id !== id);
    saveAll(all);
  },

  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  },
};
